import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Alert, 
  Typography, 
  Space, 
  message,
  Descriptions,
  Divider
} from 'antd';
import { 
  LockOutlined, 
  ReloadOutlined, 
  CopyOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import axios from '../api';

const { Title, Text, Paragraph } = Typography;

const PatientPortalPasswordReset = ({ patient, onPasswordReset }) => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [form] = Form.useForm();
  const { token } = useAuth();

  // Check if patient has contact info for password delivery
  const hasContactInfo = patient?.contactInfo?.email || patient?.contactInfo?.phone;

  const handlePasswordReset = async (values) => {
    try {
      setLoading(true);

      // Use the global axios instance which already has the correct baseURL configured
      const response = await axios.post(
        `/patients/${patient._id}/reset-portal-password`,
        {
          reason: values.reason || 'Admin password reset'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setResetResult(response.data.data);
      form.resetFields();

      const successMessage = response.data.data.isNewAccess
        ? 'Portal access created successfully!'
        : 'Portal password reset successfully!';
      message.success(successMessage);

      if (onPasswordReset) {
        onPasswordReset(response.data.data);
      }

    } catch (error) {
      console.error('Error resetting portal password:', error);
      message.error(error.response?.data?.message || 'Failed to reset portal password');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard!`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setResetResult(null);
    form.resetFields();
  };

  if (!hasContactInfo) {
    return (
      <Card 
        title={
          <Space>
            <LockOutlined />
            <Title level={5} style={{ margin: 0 }}>Portal Password Reset</Title>
          </Space>
        }
        size="small"
      >
        <Alert
          message="Cannot Reset Password"
          description="Patient must have either an email address or phone number to receive the new password."
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  return (
    <>
      <Card 
        title={
          <Space>
            <LockOutlined />
            <Title level={5} style={{ margin: 0 }}>Portal Password Reset</Title>
          </Space>
        }
        size="small"
        extra={
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => setModalVisible(true)}
            size="small"
          >
            {hasContactInfo ? 'Reset/Create Password' : 'Cannot Reset'}
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            {hasContactInfo ?
              "Reset the patient's portal password or create initial portal access. A new secure password will be generated and can be sent to the patient." :
              "Patient needs email or phone number to receive portal access credentials."
            }
          </Text>
          
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="Email">
              {patient.contactInfo?.email ? (
                <Space>
                  <MailOutlined />
                  <Text>{patient.contactInfo.email}</Text>
                </Space>
              ) : (
                <Text type="secondary">Not provided</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {patient.contactInfo?.phone ? (
                <Space>
                  <PhoneOutlined />
                  <Text>{patient.contactInfo.phone}</Text>
                </Space>
              ) : (
                <Text type="secondary">Not provided</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Card>

      {/* Password Reset Modal */}
      <Modal
        title="Reset Portal Password"
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        {!resetResult ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handlePasswordReset}
          >
            <Alert
              message="Create/Reset Portal Password"
              description={
                <div>
                  <p>This will generate a new secure password for the patient's portal access. If the patient doesn't have portal access yet, it will be created automatically.</p>
                  <p><strong>Patient:</strong> {patient.firstName} {patient.lastName}</p>
                  <p><strong>Contact:</strong> {patient.contactInfo?.email || patient.contactInfo?.phone}</p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="reason"
              label="Reason for Reset (Optional)"
              help="This will be logged for audit purposes"
            >
              <Input.TextArea
                placeholder="e.g., Patient forgot password, security concern, etc."
                rows={3}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<ReloadOutlined />}
                >
                  Create/Reset Password
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <div>
            <Alert
              message={resetResult.isNewAccess ? "Portal Access Created" : "Portal Access Ready"}
              description={resetResult.isNewAccess
                ? "Portal access has been created for this patient. A new password has been generated and the patient can now log in using their email/phone and this password."
                : "A new password has been generated for the patient's portal access. The patient can now log in using their email/phone and this password."
              }
              type="success"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Title level={5}>New Password</Title>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Text code style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {resetResult.newPassword}
                  </Text>
                  <Button 
                    type="text" 
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(resetResult.newPassword, 'Password')}
                  />
                </div>
              </div>

              <Divider />

              <div>
                <Title level={5}>Patient Contact Information</Title>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Email">
                    {resetResult.patientContact.email || 'Not provided'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {resetResult.patientContact.phone || 'Not provided'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reset By">
                    {resetResult.resetBy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reset Time">
                    {new Date(resetResult.resetAt).toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>
              </div>

              <Alert
                message="Next Steps"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Provide the new password to the patient via their preferred contact method</li>
                    <li>Direct the patient to the portal login page: <strong>/portal/login</strong></li>
                    <li>Patient can log in using their email/phone number and this password</li>
                    <li>Advise the patient to change their password after logging in for security</li>
                    <li>This action has been logged for audit purposes</li>
                  </ul>
                }
                type="info"
                showIcon
              />
            </Space>

            <div style={{ textAlign: 'right', marginTop: 24 }}>
              <Button type="primary" onClick={handleCloseModal}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default PatientPortalPasswordReset;
